import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import dynamic from 'next/dynamic';
import MetaTag from 'components/meta';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import isNil from 'lodash/isNil';
import InputText from 'components/common/InputText';
import Wrapper from 'components/common/Wrapper';
import Button from 'components/common/button';
import dayjs, { Dayjs } from 'dayjs';
import PhoneInput from 'components/common/PhoneInput';
import {
  formatDateOnly,
  isEmptyString,
  parseErrorMessage,
  phoneValidation,
  clearAppLocalStorage,
} from 'utils/helper';
import { communiqueAction } from 'reduxStateSlice/communique';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { organizationMediaAction } from 'reduxStateSlice/organizationMedia';
import { organizationAction } from 'reduxStateSlice/organization';
import { ModuleTypes } from 'types/appState';
import {
  CommuniqueByPatientInfoRequestDTO,
  OrganizationDTO,
} from 'artifacts/dtos';
import axios from 'axios';
import { RootState } from 'store/store';
import { defaultIpadBaseIntakeUrl } from 'env';
import clsx from 'clsx';
import ChoiceScreen, { CallingScreen } from 'components/communiqueByPatientInfo/ChoiceScreen';
import HeaderLogo from 'components/common/headerLogo';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {
  useGetOrgBannerPictureByPatientId,
  useGetOrgByIpadBaseIntakeUrl,
  useGetTokenByDemographics,
  useGetOrCreateCommuniqueByPatientInfoAndUpdateAppointment,
} from 'hooks/useApi';

const MuiDatePicker = dynamic(() => import('components/common/datePicker'), { ssr: false });
const noContentErrorMessage = 'Your information is incorrect. Please verify the details and try again.';
type NullableOrganizationDTO = OrganizationDTO | null;
export const phoneMask = [{
  mask: '+{1} (000) 000-0000',
  lazy: false,
  overwrite: true,
}];

// Set CSS variable for viewport height (fixes iOS viewport issues)
const setViewportHeight = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

type PatientInfoForm = {
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth?: Dayjs;
  phoneNumber: string;
}

type ScreenState = 'form' | 'choice' | 'done';

function CommuniqueByPatientInfo() {
  const [isAiCalled, setAiCalled] = useState(false);

  const organization = useSelector(
    (state: RootState) => state.organization,
  ) as NullableOrganizationDTO;

  const { page } = useSelector((state: RootState) => state.appStateSettings);

  const dispatch = useDispatch();
  const formProps = useForm<PatientInfoForm>({
    mode: 'onChange',
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isLoading, isSubmitting },
  } = formProps;
  const { mutateAsync: getTokenByDemographics } = useGetTokenByDemographics();
  const [errorMessage, setErrorMessage] = useState('');
  const [orgSettingError, setOrgSettingError] = useState(false);
  const [hasRachelIntakeIntegration, setHasRachelIntakeIntegration] = useState(false);
  const [screen, setScreen] = useState<ScreenState>((page as ScreenState) ?? 'form');
  const [doneNote, setDoneNote] = useState<string>('');
  const { mutateAsync: getOrgBannerPictureByPatientId } = useGetOrgBannerPictureByPatientId();
  const { mutateAsync: getOrgByIpadBaseIntakeUrl } = useGetOrgByIpadBaseIntakeUrl();
  const {
    mutateAsync: patientInfoAndUpdateAppointment,
  } = useGetOrCreateCommuniqueByPatientInfoAndUpdateAppointment();

  useEffect(() => {
    if (page) {
      setScreen(page as ScreenState);
    }
  }, [page]);

  const getOrganizationByUrl = async () => {
    try {
      const orgInfo = await getOrgByIpadBaseIntakeUrl({
        ipadBaseIntakeUrl: !isEmptyString(defaultIpadBaseIntakeUrl)
          ? defaultIpadBaseIntakeUrl : window.location.hostname,
      });
      dispatch(organizationAction(orgInfo.organization));
      setHasRachelIntakeIntegration(orgInfo.hasRachelIntakeIntegration ?? false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(parseErrorMessage(error));
        setOrgSettingError(true);
      }
    }
  };

  useEffect(() => {
    getOrganizationByUrl();
  }, []);

  useEffect(() => {
    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);

    return () => {
      window.removeEventListener('resize', setViewportHeight);
    };
  }, []);

  const handleCheckIn = async (values: PatientInfoForm) => {
    setErrorMessage('');
    setAiCalled(false);
    setDoneNote('');
    const payload: CommuniqueByPatientInfoRequestDTO = {
      ...values,
      dateOfBirth: formatDateOnly(values.dateOfBirth as Dayjs),
      phoneNumber: `+${values.phoneNumber}`,
      organizationId: organization?.organizationId ?? 0,
    };

    try {
      await getTokenByDemographics(payload);
      const data = await patientInfoAndUpdateAppointment(payload);
      dispatch(communiqueAction(data));
      dispatch(appStateSettingsAction({
        appointmentToken: data?.token,
        appointmentPatientId: data?.patientId,
      }));
      const banner = await getOrgBannerPictureByPatientId(data?.patientId ?? 0);
      dispatch(organizationMediaAction(banner?.list ?? []));
      setScreen('choice');
    } catch (error) {
      if (axios.isAxiosError(error) && error.code?.toString() === '400') {
        setErrorMessage(parseErrorMessage(error));
        return;
      }

      const _errorMessage = parseErrorMessage(
        error,
        noContentErrorMessage,
      );
      setErrorMessage(_errorMessage);
    }
  };

  const resetState = useCallback(async () => {
    // reset state to initial screen
    reset();
    await clearAppLocalStorage();
    dispatch(communiqueAction(null));
    setAiCalled(false);
    setDoneNote('');
    setErrorMessage('');
    setOrgSettingError(false);
    setScreen('form');
  }, [dispatch, reset]);

  const handleRedirectToForm = useCallback(() => {
    setTimeout(async () => {
      resetState();
    }, 5000);
  }, [resetState]);

  const handleDone = useCallback(async () => {
    setAiCalled(false);
    setDoneNote('');
    setScreen('done');
    handleRedirectToForm();
  }, [handleRedirectToForm]);

  const disabled = useMemo(
    () => !isValid || isLoading || isSubmitting,
    [isValid, isLoading, isSubmitting],
  );

  const titleText = useMemo(() => `Welcome to ${organization?.name ?? 'Populate'}`, [organization]);
  const showCheckInError = !orgSettingError
    && screen === 'form'
    && !isEmptyString(errorMessage);
  const isNoAppointmentError = /appointment|incorrect|not found|no content|verify/i.test(
    errorMessage,
  );
  const checkInErrorTitle = isNoAppointmentError
    ? 'No appointment found for today'
    : 'Unable to check in';
  const checkInErrorDescription = isNoAppointmentError
    ? 'Please check your name, date of birth, and phone'
    : errorMessage;

  const handleAiCallSuccess = (note: string) => {
    setDoneNote(note);
    setScreen('done');
    setAiCalled(true);
  };

  const handleFillItOutInstead = () => {
    dispatch(
      appStateSettingsAction({
        name: ModuleTypes.patientList,
        page: null,
      }),
    );
  };

  return (
    <div
      className="w-full m-auto bg-gradient-to-b from-[#f7f7f7] to-white py-6 populate-check-in-page overflow-y-auto overflow-x-hidden"
      style={{
        backgroundImage: 'url(/background-shadow.webp)',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom',
        backgroundSize: 'cover',
        WebkitOverflowScrolling: 'touch',
        height: 'calc(var(--vh, 1vh) * 100)',
      }}
    >
      <div
        className={clsx(
          'px-3 md:px-0 m-auto flex justify-center',
          screen === 'choice' ? 'max-w-[600px]' : 'max-w-[500px]',
        )}
      >
        <MetaTag title={titleText} />
        <FormProvider {...formProps}>
          <div className="flex flex-col items-center gap-10 w-full">
            <div className="text-center flex flex-col gap-4 items-center">
              <div className="flex flex-col items-center">
                <h1 className="!text-lg font-bold text-[#0d968b] min-h-[40px]">
                  {organization?.name ?? ''}
                </h1>
                <div className="w-24 h-1 !bg-populate-green mx-auto rounded-full" />
              </div>
              <div className="min-h-[90px]">
                <HeaderLogo />
              </div>
            </div>
            <div className="w-full bg-white rounded-xl shadow-[0_15px_35px_0_rgba(60,66,87,.08),_0_5px_15px_0_rgba(0,0,0,.12)]">
              <div className="p-5">
                {orgSettingError && !isEmptyString(errorMessage) && (
                  <p
                    className={clsx('error-message my-4 md:my-6', { '!text-md h-screen flex items-center justify-center': orgSettingError })}
                  >
                    {errorMessage}
                  </p>
                )}
                {!orgSettingError && screen === 'form' && (
                <>
                  <h2 className="text-2xl font-bold text-[#0d968b] text-center">
                    Patient Check-In
                  </h2>
                  <p className="text-gray-500 text-center mt-2 text-lg">
                    Enter info to find your appointment
                  </p>
                  <div className="flex flex-col gap-6 w-full mt-10">
                    <Controller
                      name="firstName"
                      rules={{ required: true }}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Wrapper
                          label="First Name"
                          error={!isNil(error)}
                          id="firstName"
                          showErrorText={false}
                          className="w-full"
                          labelClasses="!text-gray-800 !font-medium !p-0 !text-base"
                        >
                          <InputText
                            {...field}
                            id="first-name"
                            type="text"
                            className="w-full kiosk-form"
                          />
                        </Wrapper>
                      )}
                    />
                    <Controller
                      name="lastName"
                      rules={{ required: true }}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Wrapper
                          label="Last Name"
                          error={!isNil(error)}
                          id="lastName"
                          showErrorText={false}
                          className="w-full"
                          labelClasses="!text-gray-800 !font-medium !p-0 !text-base"
                        >
                          <InputText
                            {...field}
                            id="last-name"
                            type="text"
                            className="w-full kiosk-form"
                          />
                        </Wrapper>
                      )}
                    />
                    <Controller
                      name="dateOfBirth"
                      rules={{ required: true, validate: (value) => dayjs(value)?.isValid() }}
                      control={control}
                      render={({ field }) => (
                        <Wrapper
                          label="Date Of Birth"
                          id="dateOfBirth"
                          showErrorText={false}
                          className="w-full"
                          labelClasses="!text-gray-800 !font-medium !p-0 !text-base"
                        >
                          <MuiDatePicker
                            {...field}
                            disableFuture
                            className="w-full"
                            size="medium"
                            sx={{
                              '& .MuiInputBase-root': { minHeight: '3rem' },
                              '& .MuiInputBase-input': { paddingY: '0.9rem' },
                            }}
                          />
                        </Wrapper>
                      )}
                    />
                    <Controller
                      name="phoneNumber"
                      rules={{ required: true, validate: phoneValidation }}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Wrapper
                          label="Phone Number"
                          id="phoneNumber"
                          showErrorText={false}
                          labelClasses="!text-gray-800 !font-medium !p-0 !text-base"
                        >
                          <PhoneInput
                            {...field}
                            mask={phoneMask}
                            id="phoneNumber"
                            className="w-full !p-[5px]"
                            error={!isNil(error)}
                            size="medium"
                            containerClassName="min-h-[3rem]"
                          />
                        </Wrapper>
                      )}
                    />
                  </div>
                  <div className="mt-5 md:mt-7">
                    {showCheckInError && (
                      <div
                        role="alert"
                        aria-live="polite"
                        className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-left"
                      >
                        <ErrorOutlineRoundedIcon className="!mt-0.5 !shrink-0 !text-2xl !text-red-600" />
                        <div>
                          <p className="mb-1 text-base font-semibold text-red-700">
                            {checkInErrorTitle}
                          </p>
                          <p className="mb-0 text-sm leading-6 text-red-600">
                            {checkInErrorDescription}
                          </p>
                        </div>
                      </div>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={disabled}
                      onClick={handleSubmit(handleCheckIn)}
                      className="!normal-case !w-full !h-12 !text-lg populate-primary-button"
                    >
                      {isSubmitting ? 'Checking In...' : 'Check In'}
                    </Button>
                    <p className="text-center text-gray-600 text-md md:text-base mt-4">
                      Need assistance? Please ask our front desk staff for help.
                    </p>
                  </div>
                </>
                )}
                {!orgSettingError && screen === 'choice' && (
                  <ChoiceScreen
                    hasRachelIntakeIntegration={hasRachelIntakeIntegration}
                    onAiCallSuccess={handleAiCallSuccess}
                    onFinishLater={handleDone}
                  />
                )}
                {!orgSettingError && screen === 'done' && (
                  isAiCalled ? (
                    <CallingScreen
                      onFillItOutInstead={handleFillItOutInstead}
                      onFinishLater={handleDone}
                    />
                  ) : (
                    <div className="flex flex-col gap-6 items-center justify-center text-center min-h-[236px] w-full">
                      <h2 className="text-2xl font-semibold">
                        Thank you for choosing
                      </h2>
                      <h2 className="text-2xl font-semibold text-populate-green">
                        {organization?.name ?? ''}
                      </h2>
                      {!isEmptyString(doneNote) && (
                        <p className="text-gray-600">
                          {doneNote}
                        </p>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </FormProvider>
      </div>
    </div>
  );
}

export default CommuniqueByPatientInfo;
